import { useMemo } from 'react';
import type { DateRange } from 'react-day-picker';
import { CalendarRange } from 'lucide-react';
import { combineLocalDateTime, formatPeriodEdge, localParts } from '@shared/lib/local-datetime';
import { cn } from '@shared/lib/utils';
import { Calendar } from './calendar';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type TableDateRangeFormat = 'date' | 'iso';

interface TableDateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (next: { dateFrom: string; dateTo: string }) => void;
  placeholder: string;
  invalid?: boolean;
  format?: TableDateRangeFormat;
  showTime?: boolean;
  timeFromLabel?: string;
  timeToLabel?: string;
  className?: string;
  compact?: boolean;
}

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** 'yyyy-MM-dd' → 'dd.MM.yyyy' без участия часовых поясов; прочее возвращаем как есть. */
const formatStoredDateLabel = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
};

const parseStoredDate = (value: string): Date | undefined => {
  if (!value) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export function TableDateRangePicker({
  dateFrom,
  dateTo,
  onChange,
  placeholder,
  invalid = false,
  format = 'date',
  showTime = false,
  timeFromLabel,
  timeToLabel,
  className,
  compact = false,
}: TableDateRangePickerProps) {
  const isoFromParts = localParts(format === 'iso' ? dateFrom : '');
  const isoToParts = localParts(format === 'iso' ? dateTo : '');
  const fromDate = format === 'date' ? parseStoredDate(dateFrom) : isoFromParts.date;
  const toDate = format === 'date' ? parseStoredDate(dateTo) : isoToParts.date;
  const range: DateRange = { from: fromDate, to: toDate };

  const label = useMemo(() => {
    if (!dateFrom) return placeholder;
    if (format === 'date') {
      const from = formatStoredDateLabel(dateFrom);
      return dateTo ? `${from} — ${formatStoredDateLabel(dateTo)}` : `${from} — …`;
    }
    return `${formatPeriodEdge(dateFrom)} — ${dateTo ? formatPeriodEdge(dateTo) : '…'}`;
  }, [dateFrom, dateTo, format, placeholder]);

  const applyRange = (next: DateRange | undefined) => {
    if (!next?.from) {
      onChange({ dateFrom: '', dateTo: '' });
      return;
    }
    if (format === 'date') {
      onChange({
        dateFrom: formatLocalDate(next.from),
        dateTo: next.to ? formatLocalDate(next.to) : '',
      });
      return;
    }
    // Выбор дат в календаре сбрасывает время: один день = 00:00–23:59 (МСК), а не «текущее время».
    onChange({
      dateFrom: combineLocalDateTime(next.from, '00:00'),
      dateTo: combineLocalDateTime(next.to ?? next.from, '23:59', true),
    });
  };

  const applyTime = (edge: 'from' | 'to', time: string) => {
    const current = edge === 'from' ? fromDate : toDate;
    if (!current || format !== 'iso') return;
    onChange({
      dateFrom:
        edge === 'from'
          ? combineLocalDateTime(current, time)
          : dateFrom,
      dateTo:
        edge === 'to'
          ? combineLocalDateTime(current, time, true)
          : dateTo,
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex min-w-0 items-center gap-2 rounded-lg border bg-background px-3 text-sm',
            compact ? 'h-9' : 'h-10',
            invalid ? 'border-destructive text-destructive' : 'border-border text-foreground',
            className,
          )}
        >
          <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto" align="start">
        <Calendar mode="range" selected={range} onSelect={applyRange} numberOfMonths={1} />
        {showTime && format === 'iso' ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Input
              type="time"
              className={compact ? 'h-9 px-3' : undefined}
              aria-label={timeFromLabel}
              value={isoFromParts.time}
              onChange={(event) => applyTime('from', event.target.value)}
            />
            <Input
              type="time"
              className={compact ? 'h-9 px-3' : undefined}
              aria-label={timeToLabel}
              value={isoToParts.time}
              onChange={(event) => applyTime('to', event.target.value)}
            />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
